import { useEffect } from 'react';
import Router from 'next/router';

export type IframeHeightGuardUI = {
  iframeHeight: number | null;
  suppressIframeHeightShrinkDispatch: boolean;
};

// True when a height report would shrink the host iframe while a route transition is
// still settling -- those shrinks are mid-navigation layout artifacts (the old page torn
// down, the new one not yet laid out), not real content the host should resize to.
// Reports that grow (or match) the last dispatched height are never suppressed.
export const isSuppressedIframeHeightShrink = (ui: IframeHeightGuardUI, newHeight: number): boolean =>
  ui.suppressIframeHeightShrinkDispatch && newHeight < (ui.iframeHeight ?? 0);

export type IframeHeightDispatchUI = IframeHeightGuardUI & {
  // false when unset; a number (the pinned height) while an explicit override is active.
  iframeHeightOverride: number | false | null;
  embeddedEventDispatcher: (event: string, payload?: Record<string, unknown>) => boolean;
};

// The auto-resizer's dispatch decision, extracted so the Wrapper's integration contract
// is unit-testable without mounting its full provider tree. A measured height either
// goes out to the host and is recorded as the last dispatched height, or it is held
// back with no dispatch AND no state update: silently recording a held-back height
// would poison later comparisons against a value the host never received, so the
// settled height would never be re-dispatched after suppression clears. An explicit
// height override always wins over any measurement while it is active.
export const dispatchIframeHeightReport = (ui: IframeHeightDispatchUI, newHeight: number): void => {
  const { iframeHeightOverride: override } = ui;

  if (override && ui.iframeHeight !== override) {
    ui.iframeHeight = override;
  }

  if (!override && ui.iframeHeight !== newHeight) {
    // While a route transition settles, a shrink report is a mid-navigation layout
    // artifact (the old page torn down, the new one not yet laid out), not real
    // content. Dispatching it would shrink the host iframe mid-navigation, jumping
    // host content into view and letting scroll anchoring drag the page down as the
    // iframe grows back. Grow reports are never suppressed.
    if (isSuppressedIframeHeightShrink(ui, newHeight)) {
      return;
    }
    ui.embeddedEventDispatcher('iframe:resize', { height: newHeight });
    ui.iframeHeight = newHeight;
  }
};

// Suppresses embedded-iframe height SHRINK dispatches while a client-side route
// transition is in flight.
//
// On the first click into a route that is not yet in the client cache, the body height
// the auto-resizer measures collapses to roughly one viewport between the old page
// unmounting and the new page laying out. Reporting that collapse shrinks the host
// iframe mid-navigation, which jumps host content (e.g. the site footer) into view and
// lets scroll anchoring drag the host page down as the iframe grows back -- the customer
// lands at the bottom of the page instead of the top of the product view.
//
// While suppression is on, grow dispatches still go out immediately; shrinks are held
// back until the new route has committed and painted, at which point the settled height
// is re-measured and dispatched once via onTransitionSettled. Without that re-measure,
// a suppressed shrink would never reach the host (element-resize-detector only fires on
// size changes), leaving the iframe stuck at the pre-navigation height.
//
// The subscription hangs off the `next/router` singleton's `events` bus deliberately:
// it is the same emitter the compat context router wraps, but unlike the context
// object (which AppContainer rebuilds per navigation), its identity never changes, so
// the guard is not torn down and re-subscribed in the middle of the transition it is
// guarding.
export default function useIframeHeightTransitionGuard(
  ui: IframeHeightGuardUI,
  { isIframeSDK, onTransitionSettled }: { isIframeSDK: boolean; onTransitionSettled: () => void }
): void {
  useEffect(() => {
    if (!isIframeSDK) {
      return undefined;
    }

    const { events } = Router;
    let rafId = 0;

    const clearSuppression = (): void => {
      ui.suppressIframeHeightShrinkDispatch = false;
    };

    const resumeDispatches = (): void => {
      clearSuppression();
      onTransitionSettled();
    };

    const onRouteChangeStart = (): void => {
      // A new transition re-arms suppression, so drop any settle frames still pending
      // from the previous navigation: a stale resumeDispatches firing in the middle of
      // this one would clear the flag early and let the transient collapse dispatch.
      cancelAnimationFrame(rafId);
      ui.suppressIframeHeightShrinkDispatch = true;
    };

    // Two frames: one for the new page's committed layout, one for paint, so the settled
    // height re-measure reads real content rather than a mid-transition artifact.
    const onRouteChangeComplete = (): void => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(resumeDispatches);
      });
    };

    events.on('routeChangeStart', onRouteChangeStart);
    events.on('routeChangeComplete', onRouteChangeComplete);
    // A failed/aborted navigation leaves the old page mounted -- resume immediately.
    events.on('routeChangeError', resumeDispatches);

    return () => {
      cancelAnimationFrame(rafId);
      events.off('routeChangeStart', onRouteChangeStart);
      events.off('routeChangeComplete', onRouteChangeComplete);
      events.off('routeChangeError', resumeDispatches);
      // Never leave shrink dispatches suppressed past this subscription's lifetime.
      clearSuppression();
    };
  }, [isIframeSDK, ui, onTransitionSettled]);
}
